import { useRef } from "react";
import { PAGE_X, PAGE_Y } from "../_shared/safeArea";
import { useTrackedScreen } from "../../hooks/useTrackedScreen";
import type { SchemeTokens } from "@shared/types/foyer";
import { mareaManifest } from "@shared/schemes/manifests";
import { SchemeProvider } from "../_shared/schemeContext";
import { useScreenFocus } from "../_shared/useFocusGrid";
import { useBackHome } from "../_shared/useBackHome";
import { LauncherIcon } from "../_shared/launcher/icons";
import {
  LauncherPage,
  LauncherCustom,
  LauncherTv,
  useHomeNav,
  useLauncherClock,
  visibleFunctions,
  homeBgLayers,
  useGuestName,
  welcomeLine,
  SchemeSlot,
  launcherSlotStyle,
  type LauncherTheme,
  type LauncherScreen,
} from "../_shared/launcher/kit";
import { useNow } from "../_shared/useNow";
import { offerSlotBanner } from "../_shared/slots/offerSlot";
import type { SchemeProps } from "../registry";

/**
 * Marea - coastal resort. ARCHETYPE: vertical split - a persistent sand brand
 * panel (logo, big clock, function list) on the left; the right area shows the
 * property photo (home) or the active sub-page. Sea-glass accent, Newsreader.
 */
function mareaTheme(t: SchemeTokens): LauncherTheme & { rootBg: string } {
  const e = { ...mareaManifest.defaultTokens.extras, ...t.extras };
  return {
    serif: t.fonts.serif ?? "",
    sans: t.fonts.sans ?? "",
    surface: e.surface,
    card: e.card,
    chip: e.chip,
    line: t.palette.rule ?? "",
    ink: t.palette.ink,
    muted: t.palette.inkDim ?? "",
    accent: t.palette.accent,
    onAccent: e.onAccent,
    phTone: e.phTone,
    phInk: e.phInk,
    photoTone: "light",
    rootBg: t.palette.bg,
  };
}

export default function MareaScheme({ scheme }: SchemeProps) {
  const scopeRef = useRef<HTMLDivElement>(null);
  useScreenFocus(scopeRef);
  const [screen, setScreen] = useTrackedScreen<LauncherScreen>("home");
  useBackHome(() => setScreen("home"));
  const T = mareaTheme(scheme.tokens);
  const c = useLauncherClock();
  const guest = useGuestName();
  const { hotelName } = scheme.content.branding;
  const hero = scheme.content.images.hero;
  const bgL = homeBgLayers(scheme.content.background?.mode);
  const funcs = visibleFunctions(scheme);
  // The home promo slot, when it holds a live offer (`POS-4`). Resolved here
  // because the dock's focus chain needs to know whether there is a cell to
  // add, and `SchemeSlot` needs to know whether to draw a ring on it - one
  // answer behind both, or the remote and the picture disagree.
  // Read off the scheme directly rather than through `useOfferSlot`: this
  // component body sits OUTSIDE its own `SchemeProvider` (the provider is in
  // the tree it returns), so the context hook has nothing to read here.
  const promoClock = useNow();
  const promoBanner = offerSlotBanner(scheme, "homePromo", promoClock);
  const home = useHomeNav({
    funcs,
    screen,
    go: setScreen,
    scopeRef,
    tvPlayer: scheme.context.tvPlayer,
    headerFirst: true,
    // Last in the chain, and only while there is a live offer: an offer nobody
    // asked for may interrupt the eye, never the remote, so the ring still
    // starts on the dock.
    promo: {
      active: !!promoBanner,
      onPress: () => setScreen("offers"),
    },
  });
  const isHome = screen === "home";
  const sea = T.accent;
  const ink = T.ink;

  return (
    <SchemeProvider value={scheme}>
      <div
        ref={scopeRef}
        style={{
          position: "absolute",
          inset: 0,
          background: T.rootBg,
          color: ink,
          overflow: "hidden",
          fontFamily: T.sans,
          display: "flex",
          outline: "none",
        }}
      >
        {/* left brand panel (persistent) */}
        <div
          style={{
            width: 680,
            padding: `${PAGE_Y}px ${PAGE_X}px ${PAGE_Y}px`,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            flex: "0 0 auto",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <div>
              <div
                style={{
                  fontFamily: T.serif,
                  fontSize: 46,
                  fontWeight: 500,
                  letterSpacing: "0.04em",
                  lineHeight: 1,
                }}
              >
                {hotelName}
              </div>
              <div
                style={{
                  fontSize: 12,
                  letterSpacing: "0.26em",
                  textTransform: "uppercase",
                  color: sea,
                  marginTop: 8,
                  fontWeight: 600,
                }}
              >
                Seaside Resort
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              {home.headerIcons.map((name, k) => {
                const on = isHome && home.headerIdx === k;
                return (
                  <div
                    key={name}
                    style={{
                      padding: 7,
                      borderRadius: 10,
                      display: "flex",
                      background: on ? sea : "transparent",
                    }}
                  >
                    <LauncherIcon
                      name={name}
                      size={24}
                      color={on ? T.onAccent : ink}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ marginTop: 54 }}>
            <div
              style={{
                fontFamily: T.serif,
                fontSize: 96,
                fontWeight: 500,
                lineHeight: 1,
                letterSpacing: "-0.01em",
              }}
            >
              {c.time24}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginTop: 12,
                fontSize: 18,
                color: T.muted,
              }}
            >
              <span>
                {c.day}, {c.date}
              </span>
              <LauncherIcon name="cloud" size={22} color={sea} />
              <span>24° Sunny</span>
            </div>
          </div>

          {isHome && (
            <div
              style={{
                marginTop: 48,
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              {funcs.map((f, i) => {
                const on = home.funcIdx === i;
                return (
                  <div
                    key={f.key}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 22,
                      padding: "16px 22px",
                      borderRadius: 16,
                      background: on ? sea : "transparent",
                      color: on ? T.onAccent : ink,
                    }}
                  >
                    <LauncherIcon
                      name={f.icon}
                      size={32}
                      color={on ? T.onAccent : sea}
                      stroke={1.6}
                    />
                    <span
                      style={{
                        fontFamily: T.serif,
                        fontSize: 30,
                        fontWeight: on ? 600 : 400,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {f.label}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          <div
            style={{
              marginTop: "auto",
              fontSize: 15,
              color: T.muted,
              letterSpacing: "0.02em",
            }}
          >
            Ocean Suite 7 · Departure 14 June
          </div>
        </div>

        {/* right area */}
        {isHome ? (
          <div
            style={{
              flex: 1,
              position: "relative",
              margin: "40px 40px 40px 0",
              borderRadius: 28,
              overflow: "hidden",
              backgroundColor: bgL.photo && hero ? "#000" : T.phTone,
              backgroundImage: bgL.photo
                ? hero
                  ? `url(${hero})`
                  : `repeating-linear-gradient(135deg, ${T.phInk} 0 1px, transparent 1px 11px)`
                : undefined,
              backgroundSize: bgL.photo && hero ? "cover" : undefined,
              backgroundPosition: "center",
            }}
          >
            {bgL.tint && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(180deg, transparent 60%, rgba(20,35,31,0.5) 100%)",
                }}
              />
            )}
            {/* operator promo slot - top-right of the photo, clear of caption */}
            <SchemeSlot
              focused={home.promoFocused}
              onPress={() => setScreen("offers")}
              slotId="homePromo"
              style={launcherSlotStyle(T)}
              containerStyle={{
                position: "absolute",
                top: PAGE_Y,
                right: PAGE_X,
                width: 420,
                zIndex: 2,
              }}
            />
            <div
              style={{
                position: "absolute",
                left: PAGE_X,
                bottom: 36,
                color: "#f5f0e8",
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  letterSpacing: "0.26em",
                  textTransform: "uppercase",
                  opacity: 0.85,
                  fontWeight: 600,
                }}
              >
                {welcomeLine("Welcome back", guest)}
              </div>
              <div
                style={{
                  fontFamily: T.serif,
                  fontSize: 38,
                  marginTop: 6,
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                Calm seas, gentle breeze
              </div>
            </div>
          </div>
        ) : screen === "tv" ? (
          // Full-bleed, before LauncherPage: the built-in player carries its
          // own left-hand menu and must own the whole panel, exactly like the
          // vendor app it replaces.
          <LauncherTv theme={T} go={setScreen} scopeRef={scopeRef} />
        ) : screen === "custom" ? (
          <LauncherCustom theme={T} go={setScreen} scopeRef={scopeRef} />
        ) : (
          <div style={{ flex: 1, position: "relative" }}>
            <LauncherPage
              screen={screen}
              theme={T}
              go={setScreen}
              scopeRef={scopeRef}
            />
          </div>
        )}
      </div>
    </SchemeProvider>
  );
}
